import { useEffect } from 'react';
import { DEMO_MODE, IS_PRODUCTION } from '../../config';
import {
  classifyAccessRoute,
  isServiceWorkerSupported,
  isStandalone,
  shouldRegisterServiceWorker,
} from '../../utils/pwa';
import { useAppUpdate } from '../../hooks/useAppUpdate';
import { useServerReachability } from '../../hooks/useServerReachability';
import UpdateToast from '../UpdateToast/UpdateToast';
import ConnectionLostOverlay from '../ConnectionLostOverlay/ConnectionLostOverlay';

/**
 * PWA まわりの結線。App の直下に **1 つだけ** 置く。
 *
 * - Service Worker の登録と更新トースト（useAppUpdate + UpdateToast）
 * - サーバ到達性の監視と接続エラー案内（useServerReachability + ConnectionLostOverlay）
 *
 * ルーティングや認証には依存しないので、Router / Provider の外側に置いてよい。
 * 判定ロジックはすべて utils / hooks 側にあり、ここは配線だけを持つ。
 */
const PwaManager = () => {
  const canUseServiceWorker = shouldRegisterServiceWorker({
    isSupported: isServiceWorkerSupported(),
    isProduction: IS_PRODUCTION,
    isDemoMode: DEMO_MODE,
  });

  const { isUpdateReady, applyUpdate, dismissUpdate } = useAppUpdate(canUseServiceWorker);

  // 静的デモには /healthz も /api/health も無く、プローブすると常に異常判定になる。
  const { probe, isChecking, recheck } = useServerReachability(!DEMO_MODE);

  // 登録しない環境（dev / デモ）では、過去に登録された SW を明示的に外す。
  // localhost で本番ビルドを動かした後に dev サーバへ戻ると、古いキャッシュが
  // 居座って「直したはずのコードが反映されない」という事故になるため。
  useEffect(() => {
    if (canUseServiceWorker) return;
    if (!isServiceWorkerSupported()) return;
    void navigator.serviceWorker
      .getRegistrations()
      .then((registrations) => {
        registrations.forEach((registration) => {
          void registration.unregister();
        });
      })
      .catch(() => undefined);
  }, [canUseServiceWorker]);

  return (
    <>
      <UpdateToast open={isUpdateReady} onReload={applyUpdate} onDismiss={dismissUpdate} />
      <ConnectionLostOverlay
        probe={probe}
        isChecking={isChecking}
        route={classifyAccessRoute(window.location.hostname)}
        isPwa={isStandalone()}
        onRetry={recheck}
      />
    </>
  );
};

export default PwaManager;
